/**
 * Shared particle helpers for WebGPU SpriteNodeMaterial particles.
 * Provides soft-circle opacity node (no texture needed) and the cel-banded
 * SHARD mask — the chunky angular debris the reference VFX sheets use in
 * place of soft round puffs.
 */

import * as THREE from 'three/webgpu';
import { uv, smoothstep, float, vec3, mix, uniform, instancedBufferAttribute } from 'three/tsl';

/**
 * TSL node: soft radial circle falloff (1.0 at center, 0.0 at edge).
 */
export function softCircle() {
  const dist = uv().sub(0.5).length().mul(2.0);
  return smoothstep(float(1.0), float(0.0), dist);
}

/**
 * Create a SpriteNodeMaterial configured for particles.
 * Position is driven by the given InstancedBufferAttribute.
 *
 * The fade rides `material.userData.fade`, a uniform, because a material with an
 * `opacityNode` ignores the scalar `material.opacity` entirely. v1 animated the
 * scalar (`ScreenEffects.js:146`) and its particle bursts consequently never
 * faded — they popped out of existence at the end of their lifetime.
 *
 * Color, size and opacity are ALSO uniforms, not TSL consts. A const bakes its
 * value into the generated shader, so every distinct (color, size, opacity)
 * combo used to become its own program — on the WebGL2 fallback each one was a
 * separate synchronous GLSL compile, felt as a frame hitch on the first cast of
 * each ability. As uniforms, every particle material in the game shares ONE
 * program (per blend mode), and a pooled material can be retinted per burst via
 * `setParticleParams` instead of being rebuilt.
 */
export function createParticleMaterial(posAttr, { color, size, opacity, blending }) {
  const material = new THREE.SpriteNodeMaterial();
  const fade = uniform(1);
  const tint = uniform(color?.isColor ? color.clone() : new THREE.Color(color));
  const scale = uniform(size);
  const alpha = uniform(opacity);
  material.positionNode = instancedBufferAttribute(posAttr);
  material.scaleNode = scale;
  material.colorNode = tint;
  material.opacityNode = softCircle().mul(alpha).mul(fade);
  material.transparent = true;
  material.depthWrite = false;
  material.blending = blending === 'additive' ? THREE.AdditiveBlending : THREE.NormalBlending;
  material.userData.fade = fade;
  material.userData.tint = tint;
  material.userData.scale = scale;
  material.userData.alpha = alpha;
  return material;
}

/**
 * The shard variant: a faceted kite with hard cel bands — near-white core,
 * saturated body, deep outline — matching the chunky debris on every sheet in
 * `docs/reference/vfx/`. Soft round puffs are what made the old bursts read as
 * smoke; shards read as IMPACT.
 *
 * `rotAttr` is a per-instance vec2: (rotation radians, size multiplier). Both
 * are baked at spawn — random spin and size jitter is what stops sixteen
 * identical kites reading as a stamped pattern.
 */
export function createShardMaterial(posAttr, rotAttr, { color, size, opacity, blending }) {
  const material = new THREE.SpriteNodeMaterial();
  const fade = uniform(1);
  const tint = uniform(color?.isColor ? color.clone() : new THREE.Color(color));
  const scale = uniform(size);
  const alpha = uniform(opacity);
  const inst = instancedBufferAttribute(rotAttr);

  material.positionNode = instancedBufferAttribute(posAttr);
  material.rotationNode = inst.x;
  material.scaleNode = scale.mul(inst.y);

  const local = uv().sub(0.5).mul(2);
  // Kite distance: slightly narrow, so the shard has a direction when it spins.
  const d = local.x.abs().mul(1.45).add(local.y.abs());
  const core = smoothstep(float(0.48), float(0.4), d);
  const body = smoothstep(float(0.84), float(0.76), d);

  let col = tint.mul(0.38); // dark outline facet
  col = mix(col, tint, body);
  col = mix(col, mix(tint, vec3(1, 1, 1), 0.85), core);
  material.colorNode = col;
  material.opacityNode = smoothstep(float(1.0), float(0.94), d).mul(alpha).mul(fade);

  material.transparent = true;
  material.depthWrite = false;
  material.blending = blending === 'additive' ? THREE.AdditiveBlending : THREE.NormalBlending;
  material.userData.fade = fade;
  material.userData.tint = tint;
  material.userData.scale = scale;
  material.userData.alpha = alpha;
  return material;
}

/**
 * Re-aims a pooled particle material at a new burst: tint, sprite size, base
 * opacity, fade back to 1. No node graph is touched, so no recompile.
 * `color` accepts a hex number or a THREE.Color.
 */
export function setParticleParams(material, { color, size, opacity }) {
  const u = material.userData;
  if (!u?.tint) return;
  if (color?.isColor) u.tint.value.copy(color);
  else u.tint.value.set(color);
  u.scale.value = size;
  u.alpha.value = opacity;
  u.fade.value = 1;
}

/** Sets a particle material's fade, whether or not it was built by us. */
export function setParticleFade(material, value) {
  const fade = material.userData?.fade;
  if (fade) fade.value = value;
}
